import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Sidebar from '../components/Sidebar';

const API = 'https://affinityconcept.tech/api';

const SUGGESTIONS = [
  'What was my biggest expense last month?',
  'How much did I spend on dining this year?',
  'Am I on track with my budget?',
  'Compare my June vs May spending',
];

export default function Assistant({ token, onLogout }) {
  const [user, setUser] = useState(null);
  const [messages, setMessages] = useState([]); // { role: 'user'|'ai', text }
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  useEffect(() => {
    axios.get(`${API}/me`, { headers }).then(r => setUser(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  const send = async (text) => {
    const question = (text ?? input).trim();
    if (!question || loading) return;
    setMessages(prev => [...prev, { role: 'user', text: question }]);
    setInput('');
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/ask`, { question }, { headers, timeout: 600000 });
      setMessages(prev => [...prev, { role: 'ai', text: r.data.answer }]);
    } catch (e) {
      const msg = e.response?.data?.detail || 'Sorry, I could not get an answer right now. Please try again.';
      setMessages(prev => [...prev, { role: 'ai', text: `⚠️ ${msg}`, error: true }]);
    } finally {
      setLoading(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      <Sidebar onLogout={onLogout} user={user} />
      <main className="flex-1 flex flex-col overflow-hidden pt-12 md:pt-0">

        {/* Header */}
        <div className="px-4 md:px-8 py-4 border-b border-gray-100 bg-white">
          <h1 className="text-2xl font-bold text-gray-900">🤖 Finance Assistant</h1>
          <p className="text-gray-500 text-sm">Ask anything about your money — powered by your real data</p>
        </div>

        {/* Messages */}
        <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
          {messages.length === 0 && !loading && (
            <div className="max-w-2xl mx-auto text-center mt-8">
              <div className="text-4xl mb-3">💬</div>
              <p className="text-gray-500 text-sm mb-5">Ask a question to get started, or try one of these:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {SUGGESTIONS.map(s => (
                  <button key={s} onClick={() => send(s)}
                    className="text-left text-sm bg-white border border-gray-200 hover:border-green-400 hover:bg-green-50 rounded-xl px-4 py-3 text-gray-700 transition">
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="max-w-2xl mx-auto space-y-4">
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap ${
                  m.role === 'user'
                    ? 'bg-green-600 text-white rounded-br-sm'
                    : m.error
                      ? 'bg-red-50 text-red-600 border border-red-100 rounded-bl-sm'
                      : 'bg-white text-gray-800 border border-gray-100 shadow-sm rounded-bl-sm'
                }`}>
                  {m.text}
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-white text-gray-500 border border-gray-100 shadow-sm rounded-2xl rounded-bl-sm px-4 py-2.5 text-sm flex items-center gap-2">
                  <span className="w-3 h-3 border-2 border-gray-300 border-t-green-600 rounded-full animate-spin"></span>
                  Thinking… (this can take up to 30 seconds)
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Input */}
        <div className="px-4 md:px-8 py-4 border-t border-gray-100 bg-white">
          <div className="max-w-2xl mx-auto flex gap-2 items-end">
            <textarea
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              rows={1}
              placeholder="Ask about your spending, budget, income…"
              className="flex-1 resize-none border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500 max-h-32"
            />
            <button onClick={() => send()} disabled={loading || !input.trim()}
              className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white px-5 py-2.5 rounded-xl text-sm font-medium transition">
              Send
            </button>
          </div>
        </div>

      </main>
    </div>
  );
}
